import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { genericResponse} from '@phdsoft/common';
import { Observable, of } from 'rxjs';
import { CollectionInfoModel } from '@phdsoft/common';
import { ElementResultInfo } from 'projects/phdsoft/common/src/lib/models/element-result-info.model';
import { OfflineChangesService } from 'projects/phdsoft/common/src/lib/shared/services/offline-changes.service';
import { executionDay, localDayToUtcIso } from 'src/app/shared/utils/calendar-day.util';
import { ReferenceDateBridgeService } from 'src/app/shared/services/reference-date-bridge.service';
import { InsertionPlanChoiceState } from '../inspection-plan-assistant/insertion-plan-choice.state';
import {
  InspectionBulkDeleteRequest,
  InspectionBulkDeleteResponse
} from '../collection-inspection/_models/inspection-bulk-delete.model';

@Injectable({
  providedIn: 'root'
})
export class CollectionApi {

  readonly EndPoint = 'Inspection';

  constructor(
    private http: HttpClient,
    private readonly offlineChangesService: OfflineChangesService,
    private readonly referenceDateBridge: ReferenceDateBridgeService,
    private readonly insertionPlanChoice: InsertionPlanChoiceState
  ) { }

  // A calendar day goes to the API as that day's UTC midnight (calendar-day.util.ts).
  private formatDateToBackend(date: Date): string {
    return localDayToUtcIso(date);
  }

  saveCollectionInfo(pCollectionInfoModel: CollectionInfoModel): Observable<String> {
    return this.http.post(
      environment.API_INSPEC + `${this.EndPoint}/SaveCollectionInfo`,
      pCollectionInfoModel, { responseType: 'text' }
    )
  }

  saveInspectionElement(pinspectionElement: ElementResultInfo[], followupDate: Date): Observable<any> {
    const url = environment.API_INSPEC + `${this.EndPoint}/SaveInspectionElements`
    let parameters: any = {
      elementResultInfoViewModels: pinspectionElement,
      followupDate: followupDate ? localDayToUtcIso(followupDate) : undefined,
      // The day work executed in this save counts on, fixed now so a save queued offline
      // keeps the day it was made.
      executedOn: localDayToUtcIso(executionDay(this.referenceDateBridge.current)),
      // Where a new inspection's day is in several open plans, the one the user chose.
      preferredPlanIds: this.insertionPlanChoice.preferredPlanIds()
    }

    if (window.navigator.onLine) {
      return this.http.post(url, parameters)
    }
    else {
      this.offlineChangesService.addItem("inspectionElement", url, "post", parameters)
      return of('true')
    }
  }

  delete(pCollectionInfoModel: CollectionInfoModel): Observable<genericResponse<boolean>> {
    let collection = {
      AssetId: pCollectionInfoModel.AssetId,
      CollectionId: pCollectionInfoModel.CollectionId,
      Type: pCollectionInfoModel.Type
    };

    return this.http.post<boolean>(
      environment.API_INSPEC + `Inspection/deletecollection`,
      collection
    )
  }

  deleteCollectionElement(pCollectionId: string, pAssetId: string, pElementId: string): Observable<boolean> {
    let collection = {
      AssetId: pAssetId,
      CollectionId: pCollectionId,
      ElementId: pElementId
    };

    return this.http.post<boolean>(
      environment.API_INSPEC + `Inspection/deletecollectionelement`,
      collection
    )
  }

  deleteElementsFollowup(elements: ElementResultInfo[], startDate: Date, endDate: Date): Observable<ElementResultInfo[]> {
    const start = this.formatDateToBackend(startDate)
    const end = this.formatDateToBackend(endDate)

    return this.http.post<ElementResultInfo[]>(
      environment.API_INSPEC + `Inspection/DeleteInspectionsFollowup`,
      elements,
      {
        params: {
          startDate: start,
          endDate: end
        }
      }
    )
  }

  bulkDelete(request: InspectionBulkDeleteRequest): Observable<InspectionBulkDeleteResponse> {
    return this.http.post<InspectionBulkDeleteResponse>(
      environment.API_INSPEC + `${this.EndPoint}/BulkDelete`,
      request
    );
  }
}
